import React, { useEffect, useRef, useState } from 'react'
import Textarea from 'components/textarea'
import { MenuItem, MenuList } from 'components/menu/items'
import Popover, { popoverPlacement } from 'components/popover'
import { SaveButtons } from 'components/save-buttons'
import { PanelSwitch } from 'components/menu/panel'
import { Blank, Comment, Copy, ErrorOutline, List, Regex, Warning } from 'components/icons'
import { IContextMenuState } from 'types'
import { TEXT_COLUMN_TYPES, SELECT_COLUMN_TYPES } from 'components/spreadsheet/constants/const'
import { IColumnTypes } from 'components/spreadsheet/types'

// The validation settings shared by the column configuration UIs. Field names/types mirror the column
// model so a consumer can pass its draft/column straight through as `value` and route `onChange` patches
// to wherever it persists them.
export interface ColumnValidationValue {
  stringValidation: string | null
  validationMessage: string | null
  validationNoDuplicates: boolean
  validationNoBlanks: boolean
  hardValidation: boolean
  blockInvalidSelectValues: boolean
  displayValidationError: boolean
}

interface ColumnValidationFieldsProps {
  kind: IColumnTypes
  value: ColumnValidationValue
  onChange: (patch: Partial<ColumnValidationValue>) => void
}

// Why a text-validation pattern won't compile, or undefined when it's valid. Module-scoped because the
// React Compiler can't yet handle a conditional expression inside a try/catch.
export const getRegexError = (pattern: string | null | undefined): string | undefined => {
  try {
    new RegExp(pattern ?? '')
    return undefined
  } catch (error) {
    return error instanceof SyntaxError ? error.message : String(error)
  }
}

// The editor's width: half as wide again as a popover's own, for a sentence of a message.
const EDITOR_WIDTH = 384

// A setting that's a line of text most columns leave empty: a row with its value, or "None", at the end,
// which opens a small editor beside it, as a colour opens its picker: what it's for, the text (two lines
// of it), then Cancel and Save. Pressing the value opens it as pressing the label does.
const TextSettingRow: React.FC<{
  id: string
  inputId: string
  label: string
  description: string
  icon: React.ReactNode
  value: string | null
  placeholder: string
  onSave: (value: string | null) => void
  // Why what's typed can't be saved, if it can't: shown under the field, with Save off until it's put right.
  check?: (draft: string) => string | undefined
  // Whether surrounding spaces are taken off: not for a pattern, where a leading or trailing space can matter.
  trim?: boolean
}> = ({ id, inputId, label, description, icon, value, placeholder, onSave, check, trim = true }) => {
  const [at, setAt] = useState<IContextMenuState | null>(null)
  const [draft, setDraft] = useState<string>('')
  // Opened to type in, so the caret goes to the field.
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const open = at !== null
  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])
  const entered = trim ? draft.trim() : draft
  const problem = entered ? check?.(entered) : undefined
  const canSave = !problem && entered !== (value ?? '')
  const save = () => {
    if (!canSave) return
    onSave(entered || null)
    setAt(null)
  }
  const openEditor = (event: React.SyntheticEvent<HTMLElement>) => {
    const row = event.currentTarget.closest<HTMLElement>('[role="button"]') ?? event.currentTarget
    setDraft(value ?? '')
    setAt(popoverPlacement(row.getBoundingClientRect(), { align: 'end', width: EDITOR_WIDTH, height: 200 }))
  }

  return (
    <>
      <MenuItem
        icon={icon}
        data-testid={id}
        onClick={openEditor}
        end={
          <span className="max-w-[140px] cursor-pointer truncate font-normal text-muted" onClick={openEditor}>
            {value || 'None'}
          </span>
        }
      >
        {label}
      </MenuItem>
      <Popover
        id={`${id}-editor`}
        at={at}
        onClose={() => setAt(null)}
        nested
        width={EDITOR_WIDTH}
        label={label}
        title={label}
        description={description}
        footer={<SaveButtons saving={false} canSave={canSave} onCancel={() => setAt(null)} onSave={save} />}
      >
        <Textarea
          ref={inputRef}
          id={inputId}
          aria-label={label}
          rows={2}
          placeholder={placeholder}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          aria-invalid={!!problem}
          aria-describedby={problem ? `${inputId}-problem` : undefined}
          onKeyDown={(event) => {
            // Enter saves, as in a one-line field; Shift+Enter starts a new line.
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              save()
            }
          }}
        />
        {problem && (
          <p id={`${inputId}-problem`} className="mt-1.5 text-[12px] leading-4 text-red">
            {problem}
          </p>
        )}
      </Popover>
    </>
  )
}

// A column's validation settings, in Edit column's Data validation section: the checks as switches, as a panel's preferences are, then the text
// pattern and the message as rows showing what's set, each edited in a small popover.
// The switches read positively ("Allow …") while the stored flags are the negatives
// (validationNoDuplicates etc.), so each switch inverts its bound value.
const ColumnValidationFields: React.FC<ColumnValidationFieldsProps> = ({ kind, value, onChange }) => {
  const isText = TEXT_COLUMN_TYPES.includes(kind)
  const isSelect = SELECT_COLUMN_TYPES.includes(kind)

  return (
    <MenuList>
      <PanelSwitch
        label="Allow duplicate values"
        icon={<Copy />}
        checked={!value.validationNoDuplicates}
        onChange={(checked) => onChange({ validationNoDuplicates: !checked })}
      />
      <PanelSwitch
        label="Allow blank values"
        icon={<Blank />}
        checked={!value.validationNoBlanks}
        onChange={(checked) => onChange({ validationNoBlanks: !checked })}
      />
      {isSelect && (
        <PanelSwitch
          label="Allow values that aren't options"
          icon={<List />}
          checked={!value.blockInvalidSelectValues}
          onChange={(checked) => onChange({ blockInvalidSelectValues: !checked })}
        />
      )}
      <PanelSwitch
        label="Save entries that break these rules"
        icon={<ErrorOutline />}
        checked={!value.hardValidation}
        onChange={(checked) => onChange({ hardValidation: !checked })}
      />
      <PanelSwitch
        label="Mark invalid cells"
        icon={<Warning />}
        checked={value.displayValidationError}
        onChange={(checked) => onChange({ displayValidationError: checked })}
      />
      {isText && (
        <TextSettingRow
          id="column-regex-row"
          inputId="column-regex"
          label="Text pattern"
          description="A regular expression each entry in this column must match."
          icon={<Regex />}
          value={value.stringValidation}
          placeholder="^[A-Z]{3}-\d+$"
          check={getRegexError}
          trim={false}
          onSave={(stringValidation) => onChange({ stringValidation })}
        />
      )}
      <TextSettingRow
        id="column-validation-message-row"
        inputId="column-validation-message"
        label="Message for invalid entries"
        description="Shown on any cell in this column whose value breaks these data validation rules."
        icon={<Comment />}
        value={value.validationMessage}
        placeholder="Use the project code, such as ABC-123."
        onSave={(validationMessage) => onChange({ validationMessage })}
      />
    </MenuList>
  )
}

export default ColumnValidationFields
